// @ts-check
"use client"

import React, { useContext, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import * as Sentry from "@sentry/react";
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import AutoResizingTextarea from "@minohealth/common/components/AutoResizingTextarea";
import ProfileAvatar from "@minohealth/common/components/profile-avatar"
import AnokyeApiService from "@minohealth/common/services/AnokyeApiService"
import { showErrorMessage } from "@minohealth/common/utils/notifiers";
import { randomUUID } from "@minohealth/common/utils/random"
import { CanceledError } from "axios"
import { parseAnokyeError, getUserFriendlyMessage } from "@/services/anokye-error-handler";

import { FiCornerUpRight } from "react-icons/fi";
import { BiUpvote, BiDownvote } from "react-icons/bi";

import "./commenting-section.css"
import { SSEContext } from "../../../../utils/SSEProvider"
import { Bounce, toast } from "react-toastify"

dayjs.extend(relativeTime)

/**
 * @param {Object} props
 * @param {string} props.reportId
 * @param {string} props.mobileOverrideClass
 * 
 */
export default function CommentingSection({ reportId, mobileOverrideClass }) {
  const { comments, error, reloadComments, addComment } = useReviewComments(reportId)
  const reversedComments = comments?.slice().reverse()

   const SSEevents = useContext(SSEContext)
  
   //This listens to the events and rerender when the SSEEvent changes
   useEffect(() => {
    if (SSEevents.commentsNotification.length > 0) {
      const latestComment = SSEevents.commentsNotification[SSEevents.commentsNotification.length - 1];
      toast.info("Someone commented on your report", {
        style:{
          backgroundColor:"rgb(22, 22, 22)",
          border:"1px solid rgb(22, 53, 31) "
        },
        position: "top-center",
        autoClose: 100000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
        transition: Bounce,
        });
      
    }
    reloadComments()
  }, [SSEevents.commentsNotification]);
  
  return (
    <div className={`anokye-commenting-section ${mobileOverrideClass}`}>
      {comments == null && (
        <div className="anokye-commenting-section__loading-status">
          <p>Loading comments...</p>
        </div>
      )}
      {error != null && (
        <div className="anokye-commenting-section__error-status">
          <p>{error.message}</p>
          <button
            type="button"
            onClick={reloadComments}
          >
            <span>Retry</span>
          </button>
        </div>
      )}
      {(comments?.length == 0 && error == null) && (
        <div className="anokye-commenting-section__empty-status">
          <p>No comments yet</p>
        </div>
      )}
      {(comments != null && error == null) && (
        <CommentsList>
          {reversedComments.map((comment) => (
            <CommentItem comment={comment} key={comment._id ?? comment._clientId} />
          ))}
        </CommentsList>
      )}

      <div className="anokye-commenting-section__comments-footer">
        <CommentInput addComment={addComment} />
      </div>
    </div>
  )
}

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
function CommentsList({ children }) {
  return (
    <ul className="anokye-commenting-section__comments-list">
      {children}
    </ul>
  )
}

/**
 * @param {Object} props
 * @param {ReviewComment} props.comment 
 */
function CommentItem({ comment }) {
  const displayName = getOwnerName(comment.owner) ?? comment.owner.email
  return (
    <li
      className="anokye-commenting-section__comment-item"
      data-unsent={comment._id == null}
    >
      <div className="anokye-commenting-section__comment-item-body">
        <div className="anokye-commenting-section__comment-item-header">
          <ProfileAvatar
            className="anokye-commenting-section__comment-item-avatar"
            name={displayName}
            alt={displayName}
          />
          <p className="anokye-commenting-section__comment-item-name">
            {displayName}
          </p>
          {comment._id != null
            ? (
              <time
                className="anokye-commenting-section__comment-item-time"
                dateTime={comment.createdAt}
              >
                {dayjs(comment.createdAt).fromNow()}
              </time>
            ) : (
              <p
                className="anokye-commenting-section__comment-item-sending-status"
              >Sending...</p>
            )
          }
        </div>

        <div className="anokye-commenting-section__comment-item-content">
          <p>{comment.comment}</p>

          {/* <div className="anokye-commenting-section__comment-item-actions">
            <button
              type="button"
              className="anokye-commenting-section__comment-item-action-button"
            >
              <FiCornerUpRight />
              <span>Reply</span>
            </button>

            <ul className="anokye-commenting-section__comment-item-voting-actions-list">
              <li>
                <button
                  type="button"
                  title="Upvote"
                  className="anokye-commenting-section__comment-item-action-icon-button"
                >
                  <BiUpvote />
                  <span className="sr-only">Upvote</span>
                </button>
              </li>

              <li>
                <button
                  type="button"
                  title="Downvote"
                  className="anokye-commenting-section__comment-item-action-icon-button"
                >
                  <BiDownvote />
                  <span className="sr-only">Downvote</span>
                </button>
              </li>
            </ul>
          </div> */}
        </div>

        {/* <div className="anokye-commenting-section__comment-item-replies">
          {comment.comments?.length > 0 && (
            <CommentsList>
              {comment.comments.map((comment) => (
                <CommentItem comment={comment} key={comment._id} />
              ))}
            </CommentsList>
          )}
        </div> */}
      </div>
    </li>
  )
}

/**
 * @param {Object} props
 * @param {(comment: string) => Promise<void>} props.addComment
 */
function CommentInput({ addComment }) {
  /** @param {React.FormEvent<HTMLFormElement>} ev */
  function handleAddComment(ev) {
    ev.preventDefault()

    const form = ev.currentTarget
    if(!(form instanceof HTMLFormElement)) return
    const formData = new FormData(form)
    const comment = formData.get('comment')
    if(typeof comment !== "string") return

    addComment(comment).catch(error => {
      showErrorMessage(error.message)
      // @ts-ignore
      const commentTextarea = form.elements["comment"]
      if(!(commentTextarea instanceof HTMLTextAreaElement)) return
      commentTextarea.value = comment
      commentTextarea.focus()
    })
    form.reset()
  }

  /** @param {React.KeyboardEvent<HTMLTextAreaElement>} ev */
  function handleEnterToSubmit(ev) {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      const el = ev.target
      if(!(el instanceof HTMLElement)) return

      el.closest("form")?.requestSubmit()
    }
  }

  return (
    <form
      className="anokye-commenting-section__comment-input"
      onSubmit={handleAddComment}
    >
      <AutoResizingTextarea
        className="anokye-commenting-section__comment-input-textarea"
        name="comment"
        placeholder="Type your comment here"
        required
        onKeyDown={handleEnterToSubmit}
      />
      <button
        type="submit"
        className="anokye-commenting-section__comment-input-submit-button anokye-commenting-section__button"
      >Send</button>
    </form>
  )
}


/** @param {string} reportId */
function useReviewComments(reportId) {
  /** @type {ReactState<ReviewComment[]|null>} */
  const [comments, setComments] = useState(null)
  const [error, setError] = useState(null)
  // @ts-ignore
  const { user } = useSelector(state => state.app.datastate)
  const reloadAbortControllerRef = useRef(null)

  useEffect(() => {
    if(user?._id == null || user?.userToken == null) return

    const controller = new AbortController()
    loadComments(
      user._id,
      user.userToken,
      reportId,
      controller.signal,
    )
    return () => controller.abort()
  }, [user?._id, user?.userToken, reportId])

  useEffect(() => {
    return () => {
      if(reloadAbortControllerRef.current == null) return
      reloadAbortControllerRef.current.abort()
    }
  }, [reportId])

  /**
   * @param {string} userId
   * @param {string} userToken
   * @param {string} reportId
   * @param {AbortSignal} [signal]
   */
  async function loadComments(userId, userToken, reportId, signal) {
    setComments(null)
    setError(null)

    try {
      const { status, data } = await AnokyeApiService(userToken).GetAllComments(
        reportId, { signal }
      )
      if(!status.toString().match(/200/)) throw new Error("Failed to get comments")

      setComments(data)
    } catch (error) {
      if(error.name === "AbortError") return
      if(error instanceof CanceledError) return

      const parsedError = parseAnokyeError(error);

      Sentry.captureException(error, {
        extra: {
          context: 'CommentingSection - loadComments',
          reportId: reportId || 'undefined',
          userId: userId || 'undefined',
          userToken: userToken ? 'present' : 'missing',
          errorCode: parsedError.code,
          errorMessage: parsedError.message
        }
      });

      // Create a user-friendly error to pass to state
      const userError = new Error(getUserFriendlyMessage(parsedError.code, parsedError.message));
      // @ts-ignore - Adding custom code property
      userError.code = parsedError.code;
      setError(userError)
      setComments([])
    }
  }

  function reloadComments() {
    if(user?._id == null || user?.userToken == null) return

    const controller = new AbortController()
    reloadAbortControllerRef.current = controller
    loadComments(user?._id, user?.userToken, reportId, controller.signal)
  }

  /**
   * @param {string} comment
   */
  async function addComment(comment) {
    if(user?._id == null || user?.userToken == null) {
      throw new Error("User not logged in")
    }

    let clientId = randomUUID()
    
    setComments(prev => {
      return [
        ...prev,
        {
          _clientId: clientId,
          owner: {
            _id: user?._id,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          report: { _id: reportId },
          comment
        }
      ]
    })

    try {
      const { status, data } = await AnokyeApiService(user.userToken).PostNewComment({
        commentText: comment,
        reportId,
        commenterId: user._id
      })
      if (!status.toString().match(/^2.*/)) throw new Error("Failed to add comment")

      setComments(prev => {
        return prev.map(comment => {
          if (comment._clientId === clientId) return data.details
          return comment
        })
      })
    } catch(error) {
      const parsedError = parseAnokyeError(error);

      Sentry.captureException(error, {
        extra: {
          context: 'CommentingSection - addComment',
          reportId: reportId || 'undefined',
          commenterId: user._id || 'undefined',
          userToken: user?.userToken ? 'present' : 'missing',
          errorCode: parsedError.code,
          errorMessage: parsedError.message
        }
      });

      setComments(prev => prev.filter(comment => comment._clientId !== clientId))

      // Create a user-friendly error to throw
      const userError = new Error(getUserFriendlyMessage(parsedError.code, "Failed to add comment"));
      // @ts-ignore - Adding custom code property
      userError.code = parsedError.code;
      throw userError
    }
  }

  return {
    comments,
    reloadComments,
    error,
    addComment,
  }
}

/** @param {ReviewCommentOwner} owner */
function getOwnerName(owner) {
  if(owner.firstName != null || owner.lastName != null) {
    return [owner.firstName, owner.lastName].filter(Boolean).join(" ");
  }
  return null
}

/**
 * @typedef {Object} ReviewComment
 * @property {string} [_id]
 * @property {string} [_clientId]
 * @property {string} comment
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {ReviewCommentOwner} owner
 * @property {ReviewCommentReport} report
 */

/**
 * @typedef {Object} ReviewCommentOwner
 * @property {string} _id
 * @property {string} email
 * @property {string} [firstName]
 * @property {string} [lastName]
 */

/**
 * @typedef {Object} ReviewCommentReport
 * @property {string} _id
 */